import { useRouter } from 'expo-router';
import ActiveOffersTab from 'features/rewards/screens/MyOffers/components/ActiveOffersTab/ActiveOffersTab';
import PendingOffersTab from 'features/rewards/screens/MyOffers/components/PendingOffersTab/PendingOffersTab';
import { useMyOffersContext } from 'features/rewards/screens/MyOffers/providers/MyOffersProvider';
import {
  NativeScrollEvent,
  NativeSyntheticEvent,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';

interface OffersContentRendererProps {
  selectedTabId: string;
  hasNextPage: boolean;
  loadMore: () => void;
}

const LOAD_MORE_THRESHOLD = 200;

const OffersContentRenderer = ({
  selectedTabId,
  hasNextPage,
  loadMore,
}: OffersContentRendererProps) => {
  const router = useRouter();
  const { isOverlayLoading, isPageLoading, hasOverlayError } =
    useMyOffersContext();
  const { defaultTitle, defaultMessage, doneButtonTitle } =
    useDefaultErrorStrings();

  const handleScroll = ({
    nativeEvent,
  }: NativeSyntheticEvent<NativeScrollEvent>) => {
    const { layoutMeasurement, contentOffset, contentSize } = nativeEvent;
    const isScrollable = contentSize.height > layoutMeasurement.height;
    const isNearBottom =
      layoutMeasurement.height + contentOffset.y >=
      contentSize.height - LOAD_MORE_THRESHOLD;

    if (isScrollable && isNearBottom && hasNextPage) {
      loadMore();
    }
  };

  const content =
    selectedTabId === 'active' ? <ActiveOffersTab /> : <PendingOffersTab />;

  const overlayErrorComponent = hasOverlayError ? (
    <ErrorScreen
      screenType={ErrorScreenType.Error}
      messageHeadline={defaultTitle}
      messageBody={defaultMessage}
      primaryButton={{
        title: doneButtonTitle,
        onPress: () => router.navigate('/rewards'),
      }}
      screenAnalyticsEventKey="myActivatedOffersError"
    />
  ) : null;

  return (
    <View style={styles.container}>
      <ScrollView onScroll={handleScroll} scrollEventThrottle={16}>
        {content}
        {isPageLoading ? (
          <View style={styles.loadingContainer}>
            <LoadingSpinner />
          </View>
        ) : null}
      </ScrollView>

      <ScreenTransitionOverlay
        isLoading={isOverlayLoading}
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={overlayErrorComponent}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  loadingContainer: {
    alignItems: 'center',
    padding: theme.spacing.xs,
  },
});

export default OffersContentRenderer;
